import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from './primitives';
import { formatPrice, typeLabel, conditionClass, placeholderImg, handleImgError, getRecentlyViewed } from '../lib/utils';
import { flipState } from '../lib/flip-state';

function EquipmentCard({ item, dealers, inCompare, onToggleCompare, onEdit, onDelete, onView }) {
  // Support both embedded item.dealer (from API) and lookup from dealers list
  const dealerInfo = item.dealer || (dealers && dealers.find(d => d.id === item.dealerId || d.id === item.dealer_id));
  const isElite = dealerInfo && dealerInfo.tier === 'elite';
  const isVerified = dealerInfo && dealerInfo.verified === true;
  const hasResponseGuarantee = dealerInfo && dealerInfo.response_guarantee === true;

  return (
    <div
      className={`eq-card reveal${isElite ? ' eq-card-elite' : ''}${onView ? ' eq-card-clickable' : ''}`}
      onClick={onView ? (e) => {
        flipState.sourceRect = e.currentTarget.getBoundingClientRect();
        onView(item);
      } : undefined}
      role={onView ? 'button' : undefined}
      tabIndex={onView ? 0 : undefined}
      onKeyDown={onView ? (e => e.key === 'Enter' && onView(item)) : undefined}
      aria-label={onView ? `View details for ${item.name}` : undefined}
    >
      <div className="eq-card-image">
        <img
          src={item.image_url || placeholderImg(item.type, item.id)}
          alt={item.name}
          loading="lazy"
          decoding="async"
          className={`eq-card-img ${item.image_url ? 'has-real-image' : ''}`}
          onError={(e) => handleImgError(e, item.type)}
        />
        <div className="eq-card-image-overlay" />
        {item.is_featured && (
          <div className="featured-pill">Featured</div>
        )}
        {item.badge && !item.is_featured && (
          <div className="eq-card-badge">{item.badge}</div>
        )}
        <div className="eq-card-image-meta">
          <div className="eq-card-name">{item.name}</div>
          <div className="eq-card-type-tag">
            <Icon name="forklift" style={{ fontSize: 11 }} />
            {typeLabel(item.type)}
          </div>
        </div>
      </div>

      <div className="eq-card-body">
        <div className="eq-spec-grid">
          <div className="eq-spec-cell">
            <span className="label">Capacity</span>
            <span className="value">{item.capacity || '—'}</span>
          </div>
          <div className="eq-spec-cell">
            <span className="label">Fuel</span>
            <span className="value">{item.fuel || '—'}</span>
          </div>
          <div className="eq-spec-cell">
            <span className="label">Price</span>
            <span className="value value-price">{formatPrice(item.price)}</span>
          </div>
          <div className="eq-spec-cell">
            <span className="label">Condition</span>
            <span className={`tag-condition ${conditionClass(item.condition)}`}>
              {item.condition || '—'}
            </span>
          </div>
        </div>

        {dealerInfo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--on-surface-var)' }}>
              <Icon name="storefront" style={{ fontSize: 13 }} />
              <span>{dealerInfo.company}</span>
            </div>
            {isVerified && (
              <span className="verified-badge">
                <Icon name="verified" />
                Verified
              </span>
            )}
            {hasResponseGuarantee && (
              <span className="response-badge">
                <Icon name="schedule" />
                Responds in 24hrs
              </span>
            )}
          </div>
        )}

        <div className="eq-card-actions">
          <label
            className="eq-compare-check"
            title="Add to Compare"
            onClick={e => e.stopPropagation()}
          >
            <input type="checkbox" checked={inCompare} onChange={() => onToggleCompare(item)} />
            <div className={`check-box ${inCompare ? 'checked' : ''}`}>
              {inCompare && <Icon name="check" />}
            </div>
            <span>Compare</span>
          </label>

          <div style={{ flex: 1 }} />

          {onEdit && (
            <button
              className="btn-icon"
              title="Edit"
              onClick={e => { e.stopPropagation(); onEdit(item); }}
              aria-label={`Edit ${item.name}`}
            >
              <Icon name="edit" />
            </button>
          )}
          {onDelete && (
            <button
              className="btn-icon"
              title="Delete"
              style={{ color: 'var(--error)' }}
              onClick={e => { e.stopPropagation(); onDelete(item); }}
              aria-label={`Delete ${item.name}`}
            >
              <Icon name="delete" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RecentlyViewed({ onViewEquipment }) {
  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    setItems(getRecentlyViewed());
  }, []);

  if (items.length === 0) return null;

  return (
    <section style={{
      padding: '48px 32px',
      maxWidth: 1536,
      margin: '0 auto',
    }} className="reveal">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: 24,
      }}>
        <div>
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'var(--primary)',
            display: 'block',
            marginBottom: 8,
          }}>Your Recent Activity</span>
          <h2 style={{
            fontFamily: 'var(--font-head)',
            fontSize: 'clamp(22px, 3vw, 28px)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '-0.02em',
          }}>Recently Viewed</h2>
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 12,
      }}>
        {items.map(item => (
          <button
            key={item.id}
            onClick={() => onViewEquipment && onViewEquipment(item.id)}
            style={{
              background: 'var(--surface-container)',
              border: '1px solid var(--outline-var)',
              borderRadius: 'var(--radius-lg)',
              padding: 12,
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              transition: 'transform var(--transition-fast), box-shadow var(--transition-fast), border-color var(--transition-fast)',
              boxShadow: 'var(--shadow-sm)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              e.currentTarget.style.borderColor = 'var(--primary-dim)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              e.currentTarget.style.borderColor = 'var(--outline-var)';
            }}
          >
            <div style={{
              width: '100%',
              height: 100,
              background: 'var(--surface-high)',
              borderRadius: 'var(--radius)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt={item.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => handleImgError(e, item.type)}
                />
              ) : (
                <Icon name="forklift" style={{ fontSize: 32, color: 'var(--outline-var)' }} />
              )}
            </div>
            <div style={{
              fontFamily: 'var(--font-head)',
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--on-surface)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>{item.name}</div>
            <div style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--primary)',
              fontFamily: 'var(--font-body)',
            }}>{formatPrice(item.price)}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

export { EquipmentCard, RecentlyViewed };
